/* eslint-disable */
// PostgREST request-shape guards.
//
// Two independent limits bite any query that grows with the data, and both fail
// QUIETLY — which is what made the 2026-07-29 Selection bug so hard to see:
//
//   1. URL LENGTH. A `?col=in.(<uuid>,<uuid>,…)` filter is spelled out in the
//      query string. A UUID costs ~37 bytes once comma-separated, so the list grows
//      the URL linearly until the edge proxy refuses it.
//      MEASURED against this project 2026-07-29 (unauthenticated GETs, so the
//      boundary is the proxy's and not RLS):
//          176 ids →  6,591 chars → 200
//          350 ids → 13,029 chars → 200      ← last size that works
//          400 ids → 14,879 chars → REJECTED ← connection refused, no response
//          657 ids → 24,388 chars → REJECTED
//      So the real ceiling is ~16 KB of URL, i.e. ~350-400 UUIDs — NOT the ~8 KB /
//      ~200 that gets quoted for this class of bug. In the browser the rejection
//      surfaces as a failed load / 400 with no hint which limit was hit.
//   2. ROW CAP. PostgREST returns at most 1,000 rows per request by default. Past
//      that a list silently truncates and reads as complete.
//
// Prefer a server-side join over sending ids at all (a FK + an `!inner` embed does
// it in one short URL, with no threshold to cross again). Use these helpers when a
// join genuinely is not available.

// ── 1 · in() size guard ─────────────────────────────────────────────────────
// 200 is deliberately conservative — roughly half the measured ~350-400 ceiling.
// The headroom is the point: it leaves room for longer select= lists, extra filters
// and a longer host, all of which eat the same budget, and it fires as a named
// error naming the caller instead of a bare transport failure.
export const PG_IN_MAX = 200;

export class PgInTooLargeError extends Error {
  constructor(label, size) {
    super(
      `PostgREST in() list too large: ${label} passed ${size} ids (max ${PG_IN_MAX}). `
      + 'A list this size must use a server-side join (FK + !inner embed) or chunkIds(). '
      + 'Sending it raw produces a 400 from the edge proxy, not an empty result.'
    );
    this.name = 'PgInTooLargeError';
    this.label = label;
    this.size = size;
  }
}

// Throw loudly rather than let the request go out and 400. Returns the array so
// it can wrap an argument inline: .in('id', assertInSize(ids, 'myHook'))
export function assertInSize(ids, label) {
  const arr = ids || [];
  if (arr.length > PG_IN_MAX) throw new PgInTooLargeError(label || 'unknown', arr.length);
  return arr;
}

// Split into URL-safe batches. The caller runs them and concatenates — N round
// trips, so this is a stopgap next to a join, not an equal alternative.
export function chunkIds(ids, size = PG_IN_MAX) {
  const arr = ids || [];
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// ── 2 · row-cap guard ───────────────────────────────────────────────────────
export const PG_PAGE = 1000;      // PostgREST's default ceiling per request
const HARD_MAX = 20000;           // refuse to spin forever on a runaway query

// Page through a query with .range() until a short page arrives.
//
// `buildQuery` must return a FRESH builder each call — a supabase-js builder is a
// one-shot thenable, so reusing one across pages silently returns page 0 again.
//
// On exceeding HARD_MAX this THROWS rather than returning what it has: a truncated
// list that reads as complete is the failure mode being designed out here.
// `opts.max` lowers the ceiling for one caller (never raises it past HARD_MAX): a
// read whose completeness a figure depends on names the bound it is willing to
// walk, and fails past it rather than counting a partial set (QC inspector home,
// 2026-09-13 — see src/portals/qc/readBounds.js).
export async function fetchAllPaged(buildQuery, opts) {
  const { pageSize = PG_PAGE, label = 'query', max } = opts || {};
  const ceiling = max == null ? HARD_MAX : Math.min(max, HARD_MAX);
  const rows = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error, status } = await buildQuery().range(from, from + pageSize - 1);
    if (error) { if (error.status == null && status != null) error.status = status; throw error; } // the HTTP status rides along (readPolicy: a 4xx is never retried)
    const page = data || [];
    rows.push(...page);
    if (page.length < pageSize) break;          // short page = last page
    if (rows.length >= ceiling) {
      throw new Error(
        `fetchAllPaged(${label}) exceeded ${ceiling} rows — refusing to return a `
        + 'partial list that would read as complete. Narrow the query or paginate the UI.'
      );
    }
  }
  return rows;
}

// ── 3 · parallel row-cap guard (B1.5 hotfix, 2026-09-06) ────────────────────
// The sequential pager above fetches page N+1 only after page N has ARRIVED. On
// a long link that is the whole cost: the origin answered every page of the
// history book in ~0.2 s, and the five pages still took four minutes to land
// because each one waited for the previous transfer. This sibling asks the
// first page to COUNT (Prefer: count=exact → content-range "0-999/4306"), then
// fires the remaining pages together under a small pool and reassembles them
// in page order. Same guarantees as fetchAllPaged, and one more: the assembled
// length must equal the count, or it THROWS — a partial list must never read
// as complete (Convention #167).
//
// `buildQuery(selectOpts)` must return a FRESH builder each call and must spread
// `selectOpts` into its .select(): the pager passes { count: 'exact' } for the
// first page and {} for the rest, so the caller keeps its own column list:
//     (o) => supabase.from('t').select(COLS, o).order('id')
// Any page error rejects with THAT error object unchanged, so a caller's
// predicate on `code` / `message` (e.g. PGRST205 = table missing) still sees it.
// `opts.onProgress({ done, total })` fires after every page that lands.
async function runPool(tasks, limit) {
  const out = new Array(tasks.length);
  let next = 0;
  const worker = async () => {
    while (next < tasks.length) {
      const i = next;
      next += 1;
      out[i] = await tasks[i]();
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
  return out;
}

export async function fetchAllPagedParallel(buildQuery, opts) {
  const { pageSize = PG_PAGE, label = 'query', concurrency = 4, onProgress } = opts || {};
  const first = await buildQuery({ count: 'exact' }).range(0, pageSize - 1);
  if (first.error) throw first.error;
  const page0 = first.data || [];
  const count = typeof first.count === 'number' ? first.count : null;

  if (count === null) {
    // PostgREST did not count (no content-range total). Nothing to parallelise
    // against, so finish the way fetchAllPaged does: one page after another,
    // short page = last page, HARD_MAX still enforced.
    const rows = page0.slice();
    let page = page0;
    for (let from = pageSize; page.length === pageSize; from += pageSize) {
      if (rows.length >= HARD_MAX) {
        throw new Error(
          `fetchAllPagedParallel(${label}) exceeded ${HARD_MAX} rows — refusing to return a `
          + 'partial list that would read as complete. Narrow the query or paginate the UI.'
        );
      }
      const { data, error } = await buildQuery({}).range(from, from + pageSize - 1);
      if (error) throw error;
      page = data || [];
      rows.push(...page);
    }
    onProgress?.({ done: 1, total: 1 });
    return rows;
  }

  if (count > HARD_MAX) {
    throw new Error(
      `fetchAllPagedParallel(${label}) would return ${count} rows — over ${HARD_MAX}; refusing to `
      + 'return a partial list that would read as complete. Narrow the query or paginate the UI.'
    );
  }
  const total = Math.max(1, Math.ceil(count / pageSize));
  let done = 1;
  onProgress?.({ done, total });
  const tasks = [];
  for (let i = 1; i < total; i += 1) {
    tasks.push(async () => {
      const { data, error } = await buildQuery({}).range(i * pageSize, (i + 1) * pageSize - 1);
      if (error) throw error;
      done += 1;
      onProgress?.({ done, total });
      return data || [];
    });
  }
  const pages = await runPool(tasks, concurrency);
  const rows = page0.concat(...pages);
  if (rows.length !== count) {
    throw new Error(
      `fetchAllPagedParallel(${label}) assembled ${rows.length} of ${count} rows — refusing to `
      + 'return a partial list that would read as complete.'
    );
  }
  return rows;
}
